/* ===========================================================
   SEARCH RESULTS
   =========================================================== */
import React from "react";
import { ProductCard } from "@/components/product-card";
import { Breadcrumbs, getQuery } from "@/components/shell";
import { NETHRA_DATA } from "@/data/data";
import { Icons } from "@/lib/icons";
import { useCategories, useProducts } from "@/api/hooks";

const { useState: srUS, useMemo: srUM } = React;

function SearchPage() {
  const initialQ = getQuery("q") || "";
  const [q, setQ] = srUS(initialQ);
  const [submitted, setSubmitted] = srUS(initialQ);
  const popular = NETHRA_DATA.popularSearches;

  // Hit /api/v1/products?q=... when the user submits a query.
  const { data: productsPage, isLoading, isError } = useProducts(
    submitted ? { q: submitted, sort: "relevance", limit: 24 } : {}
  );
  const results = submitted ? (productsPage?.items ?? []) : [];
  const total = submitted ? (productsPage?.total ?? 0) : 0;

  // Match categories client-side from the API list (only 8 of them — cheap).
  const { data: categoriesPage } = useCategories();
  const matchedCats = srUM(() => {
    if (!submitted) return [];
    const cats = categoriesPage?.items ?? [];
    const needle = submitted.toLowerCase();
    return cats.filter((c) =>
      c.name.toLowerCase().includes(needle) ||
      (c.description || "").toLowerCase().includes(needle)
    );
  }, [submitted, categoriesPage]);

  const submit = (e) => {
    e.preventDefault();
    setSubmitted(q);
    const url = new URL(window.location.href);
    url.searchParams.set("q", q);
    window.history.replaceState({}, "", url);
  };

  return (
    <>
      <Breadcrumbs items={[{ label: "Search" }]} />
      <div className="container search-page">
        <form className="search-bar-big" onSubmit={submit}>
          <Icons.Search size={20} stroke={1.7} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 12,400 SKUs by name, salt or HSN…" autoFocus />
          {q && <button type="button" className="icon-btn" onClick={() => { setQ(""); setSubmitted(""); }} aria-label="Clear"><Icons.X size={16} /></button>}
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        {!submitted ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginTop: 24 }}>
            <div className="confirm-card">
              <div className="confirm-card-h">Popular searches</div>
              <div className="search-chips">
                {popular.map((p) => (
                  <a key={p} href={"/pages/storefront/search.html?q=" + encodeURIComponent(p)} className="search-chip">{p}</a>
                ))}
              </div>
            </div>
            <div className="confirm-card">
              <div className="confirm-card-h">Quick links</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <a className="search-chip" href="/pages/storefront/categories.html">All categories</a>
                <a className="search-chip" href="/pages/storefront/track-order.html">Track an order</a>
                <a className="search-chip" href="/pages/storefront/compare.html">Compare products</a>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="products-toolbar" style={{ marginBottom: 18 }}>
              <div className="products-count">
                {isLoading
                  ? <>Searching…</>
                  : isError
                  ? <>Search failed — <button className="link-button" onClick={() => window.location.reload()}>retry</button></>
                  : <><strong>{total.toLocaleString("en-IN")}</strong> results for <strong>"{submitted}"</strong></>
                }
              </div>
            </div>

            {matchedCats.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div className="kicker" style={{ marginBottom: 10 }}>Matching categories</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {matchedCats.map((c) => (
                    <a key={c.id} href={"/pages/storefront/category.html?slug=" + c.slug} className="search-chip">
                      {c.name} <span style={{ color: "var(--ink-3)" }}>· {(c.product_count || 0).toLocaleString("en-IN")}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}

            {results.length === 0 ? (
              <div className="empty">
                <div className="search-empty-art empty-ic"><Icons.Search size={32} stroke={1.6} /></div>
                <div className="empty-title">No products match "{submitted}"</div>
                <div className="empty-desc">Try a different keyword, check the spelling, or browse by category. Common stand-ins: salt name, brand, or HSN code.</div>
                <div className="search-chips" style={{ marginTop: 4, justifyContent: "center" }}>
                  {popular.slice(0, 6).map((p) => (
                    <a key={p} href={"/pages/storefront/search.html?q=" + encodeURIComponent(p)} className="search-chip">{p}</a>
                  ))}
                </div>
              </div>
            ) : (
              <div className="prod-grid cols-4">
                {results.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export { SearchPage };
